import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  $getNodeByKey,
  $getSelection,
  $isElementNode,
  $isNodeSelection,
  $isTextNode,
  $isDecoratorNode,
  $setSelection,
  $createTextNode,
  BLUR_COMMAND,
  COMMAND_PRIORITY_LOW,
  KEY_ARROW_LEFT_COMMAND,
  KEY_ARROW_RIGHT_COMMAND,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND
} from 'lexical';
import { mergeRegister } from '@lexical/utils';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

import { DropdownMenu } from '../DropdownMenu';
import { DropdownMenuItem } from '../DropdownMenuItem';
import { Avatar } from '../../atoms';
import { setMatchingEntityMentionComponent } from './MatchingEntityMentionNode';
import { sourceShape } from '../../../propsShape';
import { positionCursorAroundSpecialNode } from '../../../utils/lexicalUtils';

// Define a global callback variable for entity selection
let onEntitySelect = null;

// Export a setter function for the entity selection callback
export const setEntitySelectCallback = (callback) => {
  onEntitySelect = callback;
};

const EntityToken = styled.span`
  color: var(--primary-default) !important;
  cursor: pointer;
`;

const SelectedEntityToken = styled(EntityToken)`
  color: var(--accent-default) !important;
  font-weight: 500;
`;

const DropdownOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 150px;
`;

const MatchingEntityMentionComponent = ({
  nodeKey,
  value,
  suggestions = [],
  sources = [],
  isSelectedEntity = false
}) => {
  const entityRef = useRef(null);
  const dropdownRef = useRef(null);
  const [editor] = useLexicalComposerContext();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const getSourcePicture = useCallback(
    (name) =>
      sources?.find((source) => source?.integration.schema === name)?.integration.pictureUrl,
    [sources]
  );

  // Group suggestions by schema and then by text, keeping all matching entries
  const groupedSuggestions = useMemo(() => {
    const grouped = {};

    suggestions.forEach((suggestion) => {
      const schema = suggestion.schema || 'Other';
      const { text } = suggestion;

      if (!grouped[schema]) {
        grouped[schema] = {};
      }

      if (!grouped[schema][text]) {
        grouped[schema][text] = [];
      }

      grouped[schema][text].push(suggestion);
    });

    return grouped;
  }, [suggestions]);

  const handleEntityClick = useCallback(
    (event) => {
      // Check if entityRef.current exists and has proper bounds
      if (!entityRef.current) {
        return;
      }

      // Use the same pattern as TermTranslationComponent with click boundaries
      const rect = entityRef.current.getBoundingClientRect();
      const clickX = event.clientX;
      const elementWidth = rect.width;
      const leftBoundary = rect.left + elementWidth * 0.2; // 20% from left
      const rightBoundary = rect.left + elementWidth * 0.8; // 20% from right

      let clickZone = 'center';
      if (clickX < leftBoundary) {
        clickZone = 'left';
      } else if (clickX > rightBoundary) {
        clickZone = 'right';
      }

      // Special handling for multi-line entities
      const lineHeight = 24; // Approximate line height
      const isMultiLine = rect.height > lineHeight * 1.2;

      if (isMultiLine) {
        // For multi-line entities, check if click is within the actual text bounds
        const clickY = event.clientY;
        const isWithinVerticalBounds = clickY >= rect.top && clickY <= rect.bottom;

        if (isWithinVerticalBounds) {
          // For multi-line entities, be very aggressive - almost any click should open dropdown
          // Only exclude clicks that are clearly outside the text area
          const veryNarrowLeftBoundary = rect.left + 5; // Just 5px from left edge
          const veryNarrowRightBoundary = rect.right - 5; // Just 5px from right edge

          if (clickX >= veryNarrowLeftBoundary && clickX <= veryNarrowRightBoundary) {
            clickZone = 'center';
          } else if (clickX < veryNarrowLeftBoundary) {
            clickZone = 'left';
          } else {
            clickZone = 'right';
          }
        }
      }

      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if (node) {
          if (clickZone === 'left') {
            // Click in left zone - position cursor before
            positionCursorAroundSpecialNode(editor, node, 'before', nodeKey);
          } else if (clickZone === 'right') {
            // Click in right zone - position cursor after
            positionCursorAroundSpecialNode(editor, node, 'after', nodeKey);
          } else {
            // Click in center zone - open dropdown
            setIsDropdownOpen(true);
          }
        }
      });
    },
    [editor, nodeKey, value]
  );

  const handleOptionClick = useCallback(
    (optionText, suggestion) => {
      // Convert entity to text node with the selected option
      editor.update(
        () => {
          const node = $getNodeByKey(nodeKey);

          if (node) {
            // Create a text node with the selected option text
            const textNode = $createTextNode(optionText);

            // Replace the entity node with the text node
            node.replace(textNode);

            // Position cursor after the new text node
            textNode.select(optionText.length, optionText.length);

            // Call the global callback if it exists
            if (onEntitySelect) {
              onEntitySelect(optionText, suggestion);
            }
          }
        },
        { discrete: true }
      );
      setIsDropdownOpen(false);
    },
    [editor, nodeKey, value]
  );

  const onBlur = useCallback(() => {
    const node = $getNodeByKey(nodeKey);
    if (!node?.isSelected()) {
      return false;
    }

    const selection = $getSelection();
    if (!$isNodeSelection(selection)) {
      return false;
    }

    $setSelection(null);
    return false;
  }, [nodeKey]);

  const onArrowLeftPress = useCallback(
    (event) => {
      const node = $getNodeByKey(nodeKey);
      if (!node?.isSelected()) {
        return false;
      }
      let handled = false;
      const nodeToSelect = node.getPreviousSibling();
      if ($isElementNode(nodeToSelect)) {
        nodeToSelect.selectEnd();
        handled = true;
      }
      if ($isTextNode(nodeToSelect)) {
        nodeToSelect.select();
        handled = true;
      }
      if ($isDecoratorNode(nodeToSelect)) {
        nodeToSelect.selectNext();
        handled = true;
      }
      if (nodeToSelect === null) {
        node.selectPrevious();
        handled = true;
      }
      if (handled) {
        event.preventDefault();
      }
      return handled;
    },
    [nodeKey]
  );

  const onArrowRightPress = useCallback(
    (event) => {
      const node = $getNodeByKey(nodeKey);
      if (!node?.isSelected()) {
        return false;
      }
      let handled = false;
      const nodeToSelect = node.getNextSibling();
      if ($isElementNode(nodeToSelect)) {
        nodeToSelect.selectStart();
        handled = true;
      }
      if ($isTextNode(nodeToSelect)) {
        nodeToSelect.select(0, 0);
        handled = true;
      }
      if ($isDecoratorNode(nodeToSelect)) {
        nodeToSelect.selectPrevious();
        handled = true;
      }
      if (nodeToSelect === null) {
        node.selectNext();
        handled = true;
      }
      if (handled) {
        event.preventDefault();
      }
      return handled;
    },
    [nodeKey]
  );

  const onDelete = useCallback(
    (payload) => {
      if ($isNodeSelection($getSelection())) {
        const node = $getNodeByKey(nodeKey);
        if (node && node.isSelected()) {
          payload.preventDefault();
          node.remove();
          return true;
        }
      }
      return false;
    },
    [nodeKey]
  );

  useEffect(() => {
    const unregister = mergeRegister(
      editor.registerCommand(KEY_ARROW_LEFT_COMMAND, onArrowLeftPress, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_ARROW_RIGHT_COMMAND, onArrowRightPress, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_DELETE_COMMAND, onDelete, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_BACKSPACE_COMMAND, onDelete, COMMAND_PRIORITY_LOW),
      editor.registerCommand(BLUR_COMMAND, onBlur, COMMAND_PRIORITY_LOW)
    );
    return () => {
      unregister();
    };
  }, [editor, onArrowLeftPress, onArrowRightPress, onDelete, onBlur]);

  // This function will be called when the dropdown receives a ref from Floating UI
  const setDropdownRef = useCallback((node) => {
    dropdownRef.current = node;
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only close if click is outside both the entity and the dropdown
      const isClickOutsideEntity = entityRef.current && !entityRef.current.contains(event.target);
      const isClickOutsideDropdown =
        dropdownRef.current && !dropdownRef.current.contains(event.target);

      if (isClickOutsideEntity && isClickOutsideDropdown) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Choose which styled token to use based on isSelectedEntity
  const TokenComponent = isSelectedEntity ? SelectedEntityToken : EntityToken;

  return (
    <>
      <TokenComponent
        ref={entityRef}
        style={{ color: 'var(--primary-default)' }}
        onClick={handleEntityClick}
        data-entity-mention='true'
      >
        {value}
      </TokenComponent>

      <DropdownMenu
        ref={setDropdownRef}
        isOpen={isDropdownOpen}
        referenceElement={entityRef.current}
        menuPlacement='bottom-start'
      >
        <DropdownOptionsContainer>
          {Object.keys(groupedSuggestions).length > 0 ? (
            Object.entries(groupedSuggestions).map(([schema, textGroups]) => (
              <React.Fragment key={schema}>
                {Object.entries(textGroups).map(([text, textSuggestions]) => (
                  <DropdownMenuItem
                    leftIcon={<Avatar src={getSourcePicture(schema)} />}
                    key={`${schema}-${text}`}
                    onClick={() => handleOptionClick(text, textSuggestions)}
                  >
                    {text}
                  </DropdownMenuItem>
                ))}
              </React.Fragment>
            ))
          ) : (
            <DropdownMenuItem disabled>No suggestions available</DropdownMenuItem>
          )}
        </DropdownOptionsContainer>
      </DropdownMenu>
    </>
  );
};

// Register the component
setMatchingEntityMentionComponent(MatchingEntityMentionComponent);

MatchingEntityMentionComponent.propTypes = {
  nodeKey: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  isSelectedEntity: PropTypes.bool,
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      column: PropTypes.string,
      schema: PropTypes.string,
      table: PropTypes.string,
      score: PropTypes.number
    })
  ),
  sources: PropTypes.arrayOf(sourceShape)
};

export { MatchingEntityMentionComponent };
